import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CAPTURE_WORKFLOWS,
  readStoredWorkflow,
  storeWorkflow,
} from '@/lib/capture-workflows';
import type { CaptureWorkflow } from '@/lib/api/types';

/**
 * Names the job before the shutter.
 *
 * The same nameplate is read differently for a purchase order than for a
 * stock count, and the choice made here reaches the extraction model as its
 * task instruction (`apps/capture-api/src/extraction/capture-workflows.ts`).
 * The proof of concept opened on this step, which is where the rule came
 * from; a capture with no job named still works and extracts against the
 * item standard alone.
 *
 * The choice is remembered, because an operator works a run of one job
 * rather than switching per item. The viewfinder shows it back with a way
 * to change it, so a remembered choice is never a silent one.
 *
 * A full-screen capture route (`isFullScreenRoute`), so it draws its own
 * Back: to the viewfinder when a purpose is already chosen, else to the job.
 * Each option shows its title only, per the no-subtitles rule.
 */
export function CaptureWorkflowPicker() {
  const navigate = useNavigate();
  const { jobId = '' } = useParams();
  const current = readStoredWorkflow();
  const jobPath = `/jobs/${encodeURIComponent(jobId)}`;

  function choose(workflow: CaptureWorkflow) {
    storeWorkflow(workflow);
    navigate(`${jobPath}/capture`, { replace: true });
  }

  return (
    <div className="flex flex-col gap-4 px-4 pt-2 sm:px-6 lg:px-0 lg:pt-0">
      <div className="flex items-center gap-2">
        <Link
          to={current ? `${jobPath}/capture` : jobPath}
          aria-label="Back"
          className="flex size-11 shrink-0 items-center justify-center rounded-control border border-border-strong text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <ArrowLeft className="size-5" aria-hidden="true" />
        </Link>
        <h1 className="text-lg font-medium">Purpose</h1>
      </div>

      <ul className="flex flex-col gap-2 sm:grid sm:grid-cols-2 lg:grid-cols-3">
        {CAPTURE_WORKFLOWS.map((option) => (
          <li key={option.id}>
            <button
              type="button"
              onClick={() => choose(option.id)}
              aria-current={option.id === current ? 'true' : undefined}
              className={cn(
                'flex h-full min-h-14 w-full items-center justify-between gap-2 rounded-card border border-border px-4 py-3 text-left text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                option.id === current && 'border-brand bg-brand/10',
              )}
            >
              {option.title}
              {option.id === current && <Check className="size-4 shrink-0 text-brand" aria-hidden="true" />}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
